import { get } from 'lodash-es';
import { useMemo } from 'react';
import type { EntityAdminRecord } from '../../types';
import { isNotDivider } from '../InputsAndDropdownsDrawer/components/dividerUtils';
import { flattenDrawerOptions } from '../InputsAndDropdownsDrawer/filterAndMapDrawerOptions';
import type { InputsAndDropdownsDrawerOptions } from '../InputsAndDropdownsDrawer/types';

/**
 * Hook to determine which tabs have validation errors.
 * Groups fields by their `drawerTabs` property and checks if any fields in each tab have errors and are touched.
 *
 * @param errorState - Map of field paths to error messages (null if no errors)
 * @param touchState - Map of field paths to touched state
 * @param drawerOptions - Array of drawer field options
 * @param registeredTabs - Array of registered tab labels that should be validated
 * @returns Map of tab labels to boolean indicating if the tab has validation errors
 */
export function useTabValidation<TDrawerRecord extends EntityAdminRecord>(
  errorState: Record<string, string | undefined> | null,
  touchState: Record<string, boolean>,
  drawerOptions: InputsAndDropdownsDrawerOptions<TDrawerRecord>,
  registeredTabs: string[]
): Map<string, boolean> {
  return useMemo(() => {
    const tabErrorMap = new Map<string, boolean>();

    // Group fields by their drawerTabs property
    const fieldsByTab = new Map<string, Set<string>>();

    flattenDrawerOptions(drawerOptions).forEach(option => {
      if (isNotDivider(option) && option.drawerTabs) {
        option.drawerTabs.forEach(tabName => {
          if (!fieldsByTab.has(tabName)) {
            fieldsByTab.set(tabName, new Set());
          }
          fieldsByTab.get(tabName)!.add(option.field);
        });
      }
    });

    // For each registered tab, check if it has validation errors
    registeredTabs.forEach(tabLabel => {
      const fieldsInTab = fieldsByTab.get(tabLabel) ?? new Set<string>();

      // Check if any field in this tab has an error AND is touched
      let hasError = false;
      let hasTouched = false;

      fieldsInTab.forEach(fieldPath => {
        if (errorState && get(errorState, fieldPath)) {
          hasError = true;
        }
        if (get(touchState, fieldPath)) {
          hasTouched = true;
        }
      });

      // Show error dot only if there's an error AND at least one field is touched
      tabErrorMap.set(tabLabel, hasError && hasTouched);
    });

    return tabErrorMap;
  }, [errorState, touchState, drawerOptions, registeredTabs]);
}
